import {all, call, delay, fork, put, select, takeLatest} from 'redux-saga/effects'
import {createDocumentPivotTableWatcher, createPivotTableWatcher} from 'sagas/pivotTable'
import types from 'actions/types'
import {readyForDeliveryQuotesPivotTableKey} from 'reducers/deliveriesContext/readyForDelivery'
import {
  getDeliveries,
  getDeliveriesCalendarSelectedDate,
  getDeliveriesCalendarViewRange, getDeliveriesQuoteSelectorSelectedOrder,
  getReadyForDelivery,
  getSelectedDeliveryFiltersFromState,
} from 'selectors/deliveries'
import {QuoteStatus} from 'helpers/quotes'
import {get, isEmpty, isNil} from 'lodash'
import {getInvoiceFinancialYearStart} from 'selectors/settings'
import {deliveryNotesPivotTableKey} from 'reducers/deliveriesContext/deliveryNotes'
import {getDateFilters, getDateRangeForKeyAndDate} from 'helpers/dateRange'
import {getDeliveryNoteParams} from 'helpers/deliveries/deliveryNotes'
import {getAllRows, getSearch} from 'selectors/pivotTable'
import {getNumberFromLetterSequence, getSearchTerm} from 'helpers/string'
import {
  deliveryNoteDialogInitialiseFailure,
  deliveryNoteDialogInitialiseSuccess,
  hideDeliveryNoteDialog,
  showDeleteDeliveryNoteFailurePrompt,
} from 'actions/dialog'
import {deSelectAllRows, requestData, updateData} from 'actions/pivotTable'
import {itemsPivotTableKey} from 'reducers/dialogs/deliveryNoteDialog'
import {generalError} from 'actions/index'
import * as deliveryActions from 'actions/deliveries'
import {
  addDeliveryNoteFailure,
  addDeliveryNoteSuccess,
  addDeliverySuccess,
  createNewNoteSuccess,
  deleteDeliveryNoteFailure,
  deleteDeliveryNoteSuccess,
  deleteDeliverySuccess,
  deleteDeliveryTypeNoteFailure,
  deleteDeliveryTypeNoteSuccess,
  deliveryDialogChangeTab,
  deliveryDialogInitialiseFailure,
  deliveryDialogInitialiseSuccess,
  deliveryDialogShowOperationsValidityWarning,
  hideDeliveryDialog,
  hideDeliveryTypeNoteDialog,
  loadDeliveriesFailure,
  loadDeliveriesSuccess,
  loadDeliveryCountsFailure,
  loadDeliveryCountsSuccess,
  loadDeliveryDepartmentsFailure,
  loadDeliveryDepartmentsSuccess,
  loadDeliveryWorkCentresFailure,
  loadDeliveryWorkCentresSuccess,
  markAsDeliveredFailure,
  markAsDeliveredSuccess,
  sendDeliveryNoteEmailFailure,
  sendDeliveryNoteEmailSuccess,
  showDeliveryDialog,
  updateDeliveryNoteFailure,
  updateDeliveryNoteSuccess,
  updateDeliverySuccess,
  updateDeliveryTypeNoteFailure,
  updateDeliveryTypeNoteSuccess,
} from 'actions/deliveries'
import {
  getDeliveryNoteItems, getDeliveryNoteItemsForDelivery,
  getDeliveryNoteItemsForOrderItems,
  getDeliveryNoteItemsForOrderItemsForDelivery,
} from 'helpers/deliveries/deliveryNoteItems'
import {getUser, getUserSettings} from 'selectors/auth'
import {DeliveryStatus} from 'helpers/deliveries'
import {snackbarMessage} from 'actions/snackbar'
import {iconNames} from 'components/icon'
import {ContactEmailType, getFormDataFor} from 'helpers/email/enum'
import {
  deliveriesPivotTableKey,
  deliveryActualTimesPivotTableKey,
  deliveryDocumentsPivotTableKey,
  deliveryItemsPivotTableKey,
  deliveryOperationsPivotTableKey,
} from 'reducers/deliveriesContext/deliveries'
import {
  DeliveryType, getActTime,
  getDeliveryParams,
  getDeliveryWithSortedOperations,
  getLoadDeliveryParams,
  getNewOperationForDelivery,
  getNewOperationsForEachDeliveryItem,
  getOperationsForAddRequest,
  getOperationsForUpdateRequest, isTotalQuantityZero,
  PodRequirements,
} from 'helpers/deliveries/deliveries'
import {departmentFields, workCentreFields} from 'sagas/departmentSaga'
import {DefaultDeliveryOperations, DepartmentType} from 'helpers/production'
import {getOrderDocumentsFolderPath} from 'helpers/documents'
import moment from 'moment/moment'
import {getDeliveryDialogDeliveryData, isDeliveryDialogDeliveryNew} from "selectors/dialog"
import {quotingContextDeliveryNotesPivotTableKey} from "reducers/quotingContext/selected"
import {isObserving} from "selectors/deliveries"

const deliveryNoteFields = {
  default: true,
  deliveryNoteItems: true,
  printOptions: true,
  hasDeliveries: true,
}

const orderFields = {
  default: true,
  overrides: true,
  company: {
    default: true,
    locations: true,
  },
  contact: {
    forename: true,
    surname: true,
    email: true,
    phone: true,
    mobile: true,
    location: true,
  },
  orderItems: {
    default: true,
    subitems: true,
  },
  jobCards: true,
  deliveryNotes: deliveryNoteFields,
}

const deliveryFields = {
  default: true,
  company: true,
  contact: true,
  creator: true,
  modifier: true,
  deliveryNote: {
    order: {
      default: true,
      company: true,
      contact: true,
    },
  },
  printOptions: true,
}

const singleDeliveryFields = {
  ...deliveryFields,
  deliveryNote: {
    default: true,
    order: {
      default: true,
      company: true,
      contact: true,
      deliveryNotes: {
        default: true,
        deliveryNoteItems: true,
      },
      orderitems: true,
    },
  },
  deliveryOperations: {
    default: true,
    deliveryOperationLogs: {
      default: true,
      deliveryOperationHistory: true,
    },
    deliveryOperationNotes: {
      default: true,
      creator: true,
    },
  },
}

export default (api) => {

  function* initialiseDeliveryNoteDialog({orderId, onSuccess}) {
    try {
      const response = yield call(api.quote.getOne, orderId, orderFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const order = data.result

            yield put(deliveryNoteDialogInitialiseSuccess(order))
            onSuccess && onSuccess(order)

            // Create default delivery note items for new delivery notes
            const deliveryNote = yield select(state => state.dialogs.deliveryNote.deliveryNote)
            if (isNil(deliveryNote)) {
              yield createDefaultDeliveryNoteItems(order)
            } else {
              yield injectDeliveryNoteItems(order, deliveryNote.id)
            }
          },
          function* onFail(key, message, code) {
            yield put(deliveryNoteDialogInitialiseFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(deliveryNoteDialogInitialiseFailure(e.message))
    }
  }

  function* injectDeliveryNoteItems(order, deliveryNoteId) {
    const deliveryNote = order.deliveryNotes.find(dn => dn.id === deliveryNoteId)
    const deliveryNoteItems = deliveryNote.deliveryNoteItems
        .map(deliveryNoteItem => ({
          ...deliveryNoteItem,
          orderItem: order.orderitems.find(orderItem => orderItem.id === deliveryNoteItem.orderItemId),
        }))

    yield put(updateData(itemsPivotTableKey, deliveryNoteItems))
  }

  function* createDefaultDeliveryNoteItems(order) {
    const user = yield select(getUser)
    const {returnNote} = yield select(state => state.dialogs.deliveryNote)
    const deliveryNoteItems = getDeliveryNoteItems(user, order, returnNote)
    yield put(updateData(itemsPivotTableKey, deliveryNoteItems))
  }

  function* initialiseDeliveryDialog({deliveryNoteId, orderId, delivery}) {
    try {
      if (!isNil(deliveryNoteId) && deliveryNoteId !== 'new') {
        const response = yield call(api.quote.getOne, orderId, orderFields)
        yield api.handleResponse(
            response,
            function* onOk(key, data) {
              const order = data.result
              yield injectDeliveryItems(deliveryNoteId, order)
              if (!isNil(delivery)) {
                yield injectDeliveryOperations(delivery)
              }
              yield put(deliveryDialogInitialiseSuccess())
            },
            function* onFail(key, message, code) {
              yield put(deliveryDialogInitialiseFailure(message, code))
            }
        )
      } else {
        const response = yield call(api.quote.getOne, orderId, orderFields)
        yield api.handleResponse(
            response,
            function* onOk(key, data) {
              yield createDefaultDeliveryItemsForDelivery(data.result)
              yield put(deliveryDialogInitialiseSuccess())
            },
            function* onFail(key, message, code) {
              yield put(deliveryDialogInitialiseFailure(message, code))
            }
        )
      }
    } catch (e) {
      console.error(e)
      yield put(deliveryDialogInitialiseFailure(e.message))
    }
  }

  function* createDefaultDeliveryItemsForDelivery(order) {
    const user = yield select(getUser)
    const items = getDeliveryNoteItemsForDelivery(user, order)
    yield put(updateData(deliveryItemsPivotTableKey, items))
  }

  function* injectDeliveryItems(deliveryNoteId, order) {
    const deliveryNote = order.deliveryNotes.find(deliveryNote => deliveryNote.id === deliveryNoteId)
    const items = isNil(deliveryNote)
        ? []
        : deliveryNote.deliveryNoteItems
            .map(item => ({
              ...item,
              orderItem: order.orderitems.find(orderItem => orderItem.id === item.orderItemId),
            }))
    yield put(updateData(deliveryItemsPivotTableKey, items))
  }

  function* deliveryDeleteItems({deletedItems}) {
    try {
      const items = yield select(state => getAllRows(state, deliveryItemsPivotTableKey))
      const newItems = items
          .filter(item => !deletedItems.find(deliveryItem => deliveryItem.id === item.id))
          .map((item, index) => {
            index++
            return {
              ...item,
              itemId: index,
              itemLetter: getNumberFromLetterSequence(index),
            }
          })
      yield put(updateData(deliveryItemsPivotTableKey, newItems))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deliveryAddItems({orderItems, order}) {
    try {
      const user = yield select(getUser)
      const deliveryNoteItems = yield select(state => getAllRows(state, deliveryItemsPivotTableKey))

      const jobCards = (order.jobCards || [])
      const deliveryNotes = (order.deliveryNotes || [])

      const newDeliveryNoteItems = [
        ...deliveryNoteItems,
        ...getDeliveryNoteItemsForOrderItemsForDelivery(user, order, orderItems, deliveryNotes, jobCards),
      ]
      yield put(updateData(deliveryItemsPivotTableKey, newDeliveryNoteItems))
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* injectDeliveryOperations(delivery) {
    const operations = get(delivery, 'deliveryOperations', [])
    if (!isNil(operations)) {
      yield injectDeliveryOperationLogs(operations)
    }
    yield put(updateData(deliveryOperationsPivotTableKey, operations))
  }

  function* injectDeliveryOperationLogs(operations) {
    const logsToUpdatePivotTable = operations.reduce((acc, operation) => {
      const operationLogs = get(operation, 'deliveryOperationLogs', [])
      const parsedLogs = operationLogs.map(log => {
        const instruction = get(operation, 'instruction')
        const actTime = getActTime(operation)
        return {
          ...log,
          instruction,
          actTime,
        }
      })
      return [...acc, ...parsedLogs]
    }, [])

    yield put(updateData(deliveryActualTimesPivotTableKey, logsToUpdatePivotTable))
  }

  function* deliveryAddOperations({operations}) {
    try {
      const existingOperations = yield select(state => getAllRows(state, deliveryOperationsPivotTableKey))

      const updatedOperations = [...existingOperations, ...operations]
          .map((operation, index) => ({...operation, op: index + 1}))

      yield put(updateData(deliveryOperationsPivotTableKey, updatedOperations))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deliveryDeleteOperations({deletedOperationIds}) {
    try {
      const existingOperations = yield select(state => getAllRows(state, deliveryOperationsPivotTableKey))

      const updatedOperations = existingOperations
          .filter(operation => !deletedOperationIds.find(deletedOperationId => deletedOperationId === operation.id))
          .map((operation, index) => ({...operation, op: index + 1}))

      yield put(updateData(deliveryOperationsPivotTableKey, updatedOperations))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deliveryAddOperationLogs({operationLog}) {

    try {
      const existingOperationLogs = yield select(state => getAllRows(state, deliveryActualTimesPivotTableKey))

      const updatedOperations = [...existingOperationLogs, operationLog]
          .map((log, index) => ({...log, op: index + 1}))

      yield put(updateData(deliveryActualTimesPivotTableKey, updatedOperations))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deliveryUpdateOperationLogs({operationLog}) {
    try {
      const existingOperationLogs = yield select(state => getAllRows(state, deliveryActualTimesPivotTableKey))

      const updatedOperationLogs = existingOperationLogs.map(log => {
        if (log.id === operationLog.id) {
          return {...log, ...operationLog}
        }
        return log
      })

      yield put(updateData(deliveryActualTimesPivotTableKey, updatedOperationLogs))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deliveryDeleteOperationLogs({deliveryOperationLogIds}) {

    try {
      const existingOperationLogs = yield select(state => getAllRows(state, deliveryActualTimesPivotTableKey))

      const updatedOperationLogs = existingOperationLogs
          .filter(log => !deliveryOperationLogIds.find(deletedOperationLogId => deletedOperationLogId === log.id))
          .map((log, index) => ({...log, op: index + 1}))

      yield put(updateData(deliveryActualTimesPivotTableKey, updatedOperationLogs))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addItems({orderItems}) {
    try {
      const user = yield select(getUser)
      const {order, returnNote} = yield select(state => state.dialogs.deliveryNote)
      const deliveryNoteItems = yield select(state => getAllRows(state, itemsPivotTableKey))

      const jobCards = (order.jobCards || [])
      const deliveryNotes = (order.deliveryNotes || [])

      const newDeliveryNoteItems = [
        ...deliveryNoteItems,
        ...getDeliveryNoteItemsForOrderItems(user, order, orderItems, deliveryNotes, jobCards, returnNote),
      ]

      yield put(updateData(itemsPivotTableKey, newDeliveryNoteItems))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteItems({deliveryNoteItems}) {
    try {
      const items = yield select(state => getAllRows(state, itemsPivotTableKey))
      const newItems = items
          .filter(item => !deliveryNoteItems.find(deliveryNoteItem => deliveryNoteItem.id === item.id))
          .map((deliveryNoteItem, index) => {
            index++
            return {
              ...deliveryNoteItem,
              itemId: index,
              itemLetter: getNumberFromLetterSequence(index),
            }
          })
      yield put(updateData(itemsPivotTableKey, newItems))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reset() {
    try {
      const order = yield select(state => state.dialogs.deliveryNote.order)
      yield createDefaultDeliveryNoteItems(order)

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  const mapItemsForSave = (items) => items.map(item => ({
    ...item,
    id: item.isNew ? -1 : item.id,
    guid: item.isNew ? item.id : item.guid,
  }))

  const transferOrderItems = (oldDeliveryItems, newDeliveryItems) => {
    const orderItems = oldDeliveryItems.map(deliveryItem => deliveryItem.orderItem)

    return newDeliveryItems.map(deliveryNoteItem => {
      const orderItem = orderItems.find(orderItem => orderItem.id === deliveryNoteItem.orderItemId)
      return {
        ...deliveryNoteItem,
        orderItem,
      }
    })
  }

  function* add({deliveryNote, callback}) {
    try {
      const deliveryNoteItems = yield select(state => getAllRows(state, itemsPivotTableKey))

      const params = {
        deliveryNote,
        deliveryNoteItems: mapItemsForSave(deliveryNoteItems),
        printOptions: get(deliveryNote, 'printOptions', {}),
      }

      const response = yield call(api.deliveryNote.add, params, deliveryNoteFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const deliveryNote = data.result[0]
            const newDeliveryNoteItems = transferOrderItems(deliveryNoteItems, deliveryNote.deliveryNoteItems)

            yield put(addDeliveryNoteSuccess(deliveryNote))
            yield put(updateData(itemsPivotTableKey, newDeliveryNoteItems))
            yield put(requestData(quotingContextDeliveryNotesPivotTableKey))
            callback && callback(deliveryNote)
          },
          function* onFail(key, message, code) {
            yield put(addDeliveryNoteFailure(message, code))
          }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* update({deliveryNote, isBackground = false, callback}) {
    try {
      if (isBackground) yield delay(1000)

      const deliveryNoteItems = yield select(state => getAllRows(state, itemsPivotTableKey))
      const deletedItems = yield select(state => state.dialogs.deliveryNote.deletedItems)

      const params = {
        deliveryNote,
        deliveryNoteItems: mapItemsForSave(deliveryNoteItems),
        deletedItems,
        printOptions: get(deliveryNote, 'printOptions', {}),
      }

      const response = yield call(api.deliveryNote.edit, params, deliveryNoteFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const deliveryNote = data.result[0]
            const newDeliveryNoteItems = transferOrderItems(deliveryNoteItems, deliveryNote.deliveryNoteItems)

            yield put(updateDeliveryNoteSuccess(deliveryNote))
            yield put(updateData(itemsPivotTableKey, newDeliveryNoteItems))

            callback && callback(deliveryNote)
          },
          function* onFail(key, message, code) {
            yield put(updateDeliveryNoteFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteDeliveryNote({id}) {
    try {

      const response = yield call(api.deliveryNote.delete, {id}, deliveryNoteFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(hideDeliveryNoteDialog())
            yield put(deleteDeliveryNoteSuccess(data))
          },
          function* onFail(key, message, code) {
            yield put(showDeleteDeliveryNoteFailurePrompt(message))
            yield put(deleteDeliveryNoteFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* itemsReordered({key}) {
    if (key === itemsPivotTableKey || key === deliveryItemsPivotTableKey) {
      const deliveryNoteItems = yield select(state => getAllRows(state, key))
      const updatedDeliveryNoteItems = deliveryNoteItems.map((deliveryNoteItem, index) => ({
        ...deliveryNoteItem,
        itemId: index,
        itemLetter: getNumberFromLetterSequence(index + 1),
      }))
      yield put(updateData(key, updatedDeliveryNoteItems))
    }

    if (key === deliveryOperationsPivotTableKey) {
      const operations = yield select(state => getAllRows(state, key))
      const updatedOperations = operations
          .map((operation, index) => ({...operation, op: index + 1}))
      yield put(updateData(key, updatedOperations))
    }
  }

  function* markAsDelivered({orderIds, onSuccess}) {
    try {
      yield put(snackbarMessage(
          'Marking orders as delivered...',
          iconNames.loading,
          false,
          false)
      )
      const params = {orderId: orderIds}
      const response = yield call(api.quote.markDelivered, params)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(snackbarMessage(
                'Your orders have been marked as delivered',
                iconNames.tick,
                true)
            )
            yield put(markAsDeliveredSuccess(orderIds))
            yield put(deSelectAllRows(readyForDeliveryQuotesPivotTableKey))
            onSuccess && onSuccess(orderIds)
          },
          function* onFail(key, message, code) {
            yield put(markAsDeliveredFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* sendEmail({emailDetails}) {

    try {
      const {isReturnNote} = emailDetails
      const noteName = isReturnNote ? 'return note' : 'delivery note'
      const formData = getFormDataFor(emailDetails, ContactEmailType.DELIVERY_NOTE_TEMPLATE)
      yield put(snackbarMessage(
          `Sending your ${noteName}...`,
          iconNames.loading,
          false,
          false)
      )
      const response = yield call(api.deliveryNote.sendEmail, formData)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(sendDeliveryNoteEmailSuccess(data))
            yield put(snackbarMessage(`Your ${noteName} has been sent`, iconNames.tick, true))
          },
          function* onFail(key, message, code) {
            yield put(generalError(message))
            yield put(sendDeliveryNoteEmailFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDepartments() {
    try {
      const params = {departmentType: DepartmentType.DELIVERY}

      const response = yield call(api.departments.get, params, departmentFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadDeliveryDepartmentsSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadDeliveryDepartmentsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadWorkCentres() {
    try {
      const params = {departmentType: DepartmentType.DELIVERY}

      const response = yield call(api.workCentres.get, params, workCentreFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadDeliveryWorkCentresSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadDeliveryWorkCentresFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDeliveries() {
    try {
      const selectedDate = yield select(getDeliveriesCalendarSelectedDate)
      const viewRange = yield select(getDeliveriesCalendarViewRange)
      const invoiceFinancialYearStart = yield select(getInvoiceFinancialYearStart)

      const {startDate, endDate} = getDateRangeForKeyAndDate(
          viewRange,
          selectedDate,
          invoiceFinancialYearStart,
          false,
          true
      )

      const deliveryFilters = yield select(getSelectedDeliveryFiltersFromState)
      const params = getLoadDeliveryParams(startDate, endDate, deliveryFilters)

      const response = yield call(api.delivery.get, params, deliveryFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadDeliveriesSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadDeliveriesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDelivery({id}) {
    try {
      const response = yield call(api.delivery.getOne, id, singleDeliveryFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(showDeliveryDialog({
              delivery: getDeliveryWithSortedOperations(data.result),
              isDeliveryNew: false,
            }))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem loading delivery. ' + code + ': ' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* moveDelivery({updatedDelivery}) {
    try {
      const {id, workCentreId, startDate, finishDate} = updatedDelivery
      const params = {
        delivery: {id, workCentreId, startDate, finishDate},
      }

      const response = yield call(api.delivery.move, params, deliveryFields)
      yield api.handleResponse(
          response,
          function* onOk() {
            const selectedDate = yield select(getDeliveriesCalendarSelectedDate)
            yield put(deliveryActions.loadDeliveryCounts(selectedDate))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem moving delivery. ' + code + ': ' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addDeliveryTypeNote({data}) {

    try {
      const {desc, start, end, notes, workCentre} = data
      const params = {
        delivery: {
          description: desc,
          startDate: start,
          finishDate: end,
          notes: notes,
          workCentreId: workCentre,
          deliveryType: DeliveryType.DELIVERY_NOTE,
        },
      }

      const response = yield call(api.delivery.add, params, deliveryFields)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(createNewNoteSuccess())
            yield put(hideDeliveryTypeNoteDialog())
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem creating delivery type note. ' + code + ': ' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteDeliveryTypeNote({id}) {
    try {
      const response = yield call(api.delivery.delete, {id}, deliveryNoteFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(deleteDeliveryTypeNoteSuccess(data))
            yield put(snackbarMessage('Delivery Note Deleted', iconNames.tick, true))
          },
          function* onFail(key, message, code) {
            yield put(deleteDeliveryTypeNoteFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateDeliveryTypeNote({data}) {

    try {
      const {id, desc, start, end, notes, workCentre} = data
      const params = {
        delivery: {
          id: id,
          description: desc,
          startDate: new Date(start).getTime(),
          finishDate: new Date(end).getTime(),
          notes: notes,
          workCentreId: workCentre,
          deliveryType: DeliveryType.DELIVERY_NOTE,
        },
      }

      const response = yield call(api.delivery.update, params, deliveryFields)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(updateDeliveryTypeNoteSuccess())
            yield put(hideDeliveryTypeNoteDialog())
            yield put(snackbarMessage('Delivery Note Updated', iconNames.tick, true))
          },
          function* onFail(key, message, code) {
            yield put(updateDeliveryTypeNoteFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addDelivery({delivery}) {
    try {
      const {
        address,
        deletedItemIds,
        deliveryMethodId,
        deliveryNoteId,
        finishDate,
        notes,
        orderId,
        podRequirements,
        startDate,
        status,
        title,
        trackingNumber,
        workCentreId,
      } = delivery
      const {
        address1,
        address2,
        address3,
        city,
        company,
        contact,
        country,
        county,
        phone,
        postCode,
      } = address

      let newDeliveryNoteId = deliveryNoteId
      let updateDeliveryNoteSuccess = false
      const deliveryNoteItems = yield select(state => getAllRows(state, deliveryItemsPivotTableKey))
      const deliveryOperations = yield call(prepareDeliveryOperations)

      if (isNil(deliveryOperations)) {
        return
      }

      if (deliveryNoteId === 'new') {
        const newDeliveryNote = {
          deliveryMethodId,
          orderId,
          printOptions: null,
          returnNote: false,
          scheduledDate: startDate,
          trackingNumber,
          address1,
          address2,
          address3,
          city,
          company,
          contact,
          country,
          county,
          phone,
          postCode,
        }
        const params = {
          deliveryNote: newDeliveryNote,
          deliveryNoteItems: mapItemsForSave(deliveryNoteItems),
        }
        const response = yield call(api.deliveryNote.add, params)

        newDeliveryNoteId = yield api.handleResponse(
            response,
            function* onOk(key, data) {
              const newId = data.result[0].id
              if (!isNil(newId)) {
                return newId
              } else {
                throw new Error("Invalid delivery note data")
              }
            },
            function* onFail(key, message, code) {
              yield put(snackbarMessage(
                  'Problem adding delivery note. ' + code + ': ' + message,
                  'error',
                  false))
            }
        )
      } else {
        const updatedDeliveryNote = {
          id: deliveryNoteId,
          deliveryMethodId,
          orderId,
          printOptions: null,
          returnNote: false,
          trackingNumber,
          address1,
          address2,
          address3,
          city,
          company,
          contact,
          country,
          county,
          phone,
          postCode,
        }
        const params = {
          deletedItems: deletedItemIds,
          deliveryNote: updatedDeliveryNote,
          deliveryNoteItems: mapItemsForSave(deliveryNoteItems).filter(item => !deletedItemIds.includes(item.id)),
        }
        const response = yield call(api.deliveryNote.edit, params)

        updateDeliveryNoteSuccess = yield api.handleResponse(
            response,
            function* onOk() {
              return true
            },
            function* onFail(key, message, code) {
              yield put(snackbarMessage(
                  'Problem updating delivery note. ' + code + ': ' + message,
                  'error',
                  false))
              return false
            }
        )
        if (!updateDeliveryNoteSuccess) {
          return
        }
      }

      if (isNil(newDeliveryNoteId)) {
        return
      }

      const params = {
        delivery: {
          deliveryAddress1: address1,
          deliveryAddress2: address2,
          deliveryAddress3: address3,
          deliveryCity: city,
          deliveryCompany: company,
          deliveryContact: contact,
          deliveryCountry: country,
          deliveryCounty: county,
          deliveryNoteId: newDeliveryNoteId,
          deliveryOperations: getOperationsForAddRequest(status, deliveryOperations),
          deliveryPhone: phone,
          deliveryPostCode: postCode,
          deliveryStatus: status,
          description: title,
          finishDate,
          isPhotoRequired: podRequirements === PodRequirements.PHOTO
              || podRequirements === PodRequirements.PHOTO_AND_SIGNATURE,
          isSignatureRequired: podRequirements === PodRequirements.SIGNATURE
              || podRequirements === PodRequirements.PHOTO_AND_SIGNATURE,
          notes,
          startDate,
          workCentreId,
        },
      }

      const response = yield call(api.delivery.add, params)
      yield api.handleResponse(
          response,
          function* onOk() {
            const isObservingDeliveries = yield select(isObserving)
            if (!isObservingDeliveries) {
              yield put(addDeliverySuccess())
            }
            yield put(hideDeliveryDialog())
            yield put(snackbarMessage('New delivery added', iconNames.tick))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem adding delivery. ' + code + ': ' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateDelivery({delivery}) {
    try {
      const {
        address,
        deletedItemIds,
        deliveryMethodId,
        deliveryNoteId,
        finishDate,
        id,
        notes,
        orderId,
        podRequirements,
        startDate,
        status,
        title,
        trackingNumber,
        workCentreId,
      } = delivery
      const {
        address1,
        address2,
        address3,
        city,
        company,
        contact,
        country,
        county,
        phone,
        postCode,
      } = address

      let updateDeliveryNoteSuccess = false
      const deliveryNoteItems = yield select(state => getAllRows(state, deliveryItemsPivotTableKey))
      const deliveryOperations = yield call(prepareDeliveryOperations)
      const deliveryOperationLogs = yield select(state => getAllRows(state, deliveryActualTimesPivotTableKey))

      if (isNil(deliveryOperations)) {
        return
      }

      const updatedDeliveryNote = {
        id: deliveryNoteId,
        deliveryMethodId,
        orderId,
        printOptions: null,
        returnNote: false,
        trackingNumber,
        address1,
        address2,
        address3,
        city,
        company,
        contact,
        country,
        county,
        phone,
        postCode,
      }
      const deliveryNoteUpdateParams = {
        deletedItems: deletedItemIds,
        deliveryNote: updatedDeliveryNote,
        deliveryNoteItems: mapItemsForSave(deliveryNoteItems).filter(item => !deletedItemIds.includes(item.id)),
      }
      const deliveryNoteUpdateResponse = yield call(api.deliveryNote.edit, deliveryNoteUpdateParams)

      updateDeliveryNoteSuccess = yield api.handleResponse(
          deliveryNoteUpdateResponse,
          function* onOk() {
            return true
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem updating delivery note. ' + code + ': ' + message,
                'error',
                false))
            return false
          }
      )
      if (!updateDeliveryNoteSuccess) {
        return
      }

      const deliveryUpdateParams = {
        delivery: {
          deliveryAddress1: address1,
          deliveryAddress2: address2,
          deliveryAddress3: address3,
          deliveryCity: city,
          deliveryCompany: company,
          deliveryContact: contact,
          deliveryCountry: country,
          deliveryCounty: county,
          deliveryNoteId: deliveryNoteId,
          deliveryOperations: getOperationsForUpdateRequest(deliveryOperations, deliveryOperationLogs),
          deliveryPhone: phone,
          deliveryPostCode: postCode,
          deliveryStatus: status,
          description: title,
          finishDate,
          id,
          isPhotoRequired: podRequirements === PodRequirements.PHOTO
              || podRequirements === PodRequirements.PHOTO_AND_SIGNATURE,
          isSignatureRequired: podRequirements === PodRequirements.SIGNATURE
              || podRequirements === PodRequirements.PHOTO_AND_SIGNATURE,
          notes,
          startDate,
          workCentreId,
        },
      }

      const deliveryUpdateResponse = yield call(api.delivery.update, deliveryUpdateParams)
      yield api.handleResponse(
          deliveryUpdateResponse,
          function* onOk() {
            const isObservingDeliveries = yield select(isObserving)
            if (!isObservingDeliveries) {
              yield put(updateDeliverySuccess())
            }
            yield put(hideDeliveryDialog())
            yield put(snackbarMessage('Delivery updated', iconNames.tick))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem updating delivery. ' + code + ': ' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* prepareDeliveryOperations() {
    const deliveryOperations = yield select(state => getAllRows(state, deliveryOperationsPivotTableKey))

    if (isEmpty(deliveryOperations)) {
      const deliveryNoteItems = yield select(state => getAllRows(state, deliveryItemsPivotTableKey))
      const userSettings = yield select(state => getUserSettings(state))
      const defaultOperations = get(userSettings, 'deliveriesDefaultOperations.value')

      if (isEmpty(deliveryNoteItems) || isTotalQuantityZero(deliveryNoteItems)) {
        yield call(preventDeliverySave)
        return null
      }

      switch (defaultOperations) {
        case DefaultDeliveryOperations.NONE:
          yield call(preventDeliverySave)
          return null

        case DefaultDeliveryOperations.ONE_FOR_DELIVERY:
          return [getNewOperationForDelivery(deliveryNoteItems)]

        case DefaultDeliveryOperations.ONE_FOR_EACH_DELIVERY_ITEM:
          return getNewOperationsForEachDeliveryItem(deliveryNoteItems)

        default:
          return []
      }
    }
    return deliveryOperations
  }

  function* preventDeliverySave() {
    yield put(deliveryDialogShowOperationsValidityWarning())
    yield put(deliveryDialogChangeTab('Operations'))
  }

  function* deleteDelivery({delivery}) {
    try {
      const deliveryId = get(delivery, 'id')
      const params = {id: deliveryId}

      const response = yield call(api.delivery.delete, params)
      yield api.handleResponse(
          response,
          function* onOk() {
            const isObservingDeliveries = yield select(isObserving)
            if (!isObservingDeliveries) {
              yield put(deleteDeliverySuccess())
            }
            yield put(hideDeliveryDialog())
            yield put(snackbarMessage('Your delivery has been deleted', iconNames.tick))
          },
          function* onFail(key, message, code) {
            yield put(snackbarMessage(
                'Problem deleting delivery. ' + code + ': ' + message,
                'error',
                false))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDeliveryCounts({startDate: requestedStartDate}) {
    try {
      const date = Date.parse(requestedStartDate)

      // Add +/- 15 days to show events outside the scope of the month too
      const startDate = moment(date)
          .startOf('month')
          .subtract(15, 'days')
          .valueOf()

      const endDate = moment(date)
          .startOf('month')
          .add(1, 'months')
          .add(15, 'days')
          .valueOf()

      const response = yield call(api.delivery.getSummary, {startDate, endDate}, {})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadDeliveryCountsSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadDeliveryCountsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.DELIVERY_NOTE_DIALOG_INITIALISE_ATTEMPT, initialiseDeliveryNoteDialog),
      takeLatest(types.DELIVERIES_LOAD_DELIVERY_COUNTS_ATTEMPT, loadDeliveryCounts),
      takeLatest(types.DELIVERY_NOTE_DIALOG_ADD_ITEMS, addItems),
      takeLatest(types.DELIVERY_NOTE_DIALOG_DELETE_ITEMS, deleteItems),
      takeLatest(types.DELIVERY_NOTE_DIALOG_RESET, reset),
      takeLatest(types.DELIVERIES_ADD_DELIVERY_NOTE_ATTEMPT, add),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_NOTE_ATTEMPT, update),
      takeLatest(types.DELIVERIES_DELETE_DELIVERY_NOTE_ATTEMPT, deleteDeliveryNote),
      takeLatest(types.PIVOT_TABLE_REORDER_ENTITY, itemsReordered),
      takeLatest(types.DELIVERIES_MARK_AS_DELIVERED_ATTEMPT, markAsDelivered),
      takeLatest(types.DELIVERIES_SEND_EMAIL_ATTEMPT, sendEmail),
      takeLatest(types.DELIVERIES_DEPARTMENTS_LOAD_ATTEMPT, loadDepartments),
      takeLatest(types.DELIVERIES_WORK_CENTRES_LOAD_ATTEMPT, loadWorkCentres),
      takeLatest(types.DELIVERIES_LOAD_DELIVERY_ATTEMPT, loadDelivery),
      takeLatest(types.DELIVERIES_LOAD_DELIVERIES_ATTEMPT, loadDeliveries),
      takeLatest(types.DELIVERIES_UPDATE_CALENDAR_VIEW_RANGE, loadDeliveries),
      takeLatest(types.DELIVERIES_UPDATE_CALENDAR_SELECTED_DATE, loadDeliveries),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_FILTERS, loadDeliveries),
      takeLatest(types.DELIVERIES_MOVE_DELIVERY_ATTEMPT, moveDelivery),
      takeLatest(types.DELIVERIES_ADD_DELIVERY_ATTEMPT, addDelivery),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_ATTEMPT, updateDelivery),
      takeLatest(types.DELIVERIES_DELETE_DELIVERY_ATTEMPT, deleteDelivery),
      takeLatest(types.DELIVERIES_ADD_DELIVERY_SUCCESS, loadDeliveries),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_SUCCESS, loadDeliveries),
      takeLatest(types.DELIVERIES_DELETE_DELIVERY_SUCCESS, loadDeliveries),
      takeLatest(types.DELIVERIES_CREATE_DELIVERY_TYPE_NOTE_ATTEMPT, addDeliveryTypeNote),
      takeLatest(types.DELIVERIES_CREATE_DELIVERY_TYPE_NOTE_SUCCESS, loadDeliveries),
      takeLatest(types.DELIVERIES_DELETE_DELIVERY_TYPE_NOTE_ATTEMPT, deleteDeliveryTypeNote),
      takeLatest(types.DELIVERIES_DELETE_DELIVERY_TYPE_NOTE_SUCCESS, loadDeliveries),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_TYPE_NOTE_ATTEMPT, updateDeliveryTypeNote),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_TYPE_NOTE_SUCCESS, loadDeliveries),
      takeLatest(types.DELIVERIES_DELIVERY_DIALOG_INITIALISE_ATTEMPT, initialiseDeliveryDialog),
      takeLatest(types.DELIVERIES_DELIVERY_DIALOG_DELETE_ITEMS, deliveryDeleteItems),
      takeLatest(types.DELIVERIES_DELIVERY_DIALOG_ADD_ITEMS, deliveryAddItems),
      takeLatest(types.DELIVERIES_DELIVERY_DIALOG_ADD_OPERATIONS, deliveryAddOperations),
      takeLatest(types.DELIVERIES_DELIVERY_DIALOG_DELETE_OPERATIONS, deliveryDeleteOperations),
      takeLatest(types.DELIVERIES_ADD_DELIVERY_ACTUAL_TIME_ATTEMPT, deliveryAddOperationLogs),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_ACTUAL_TIME_ATTEMPT, deliveryUpdateOperationLogs),
      takeLatest(types.DELETE_DELIVERY_ACTUAL_TIME_ATTEMPT, deliveryDeleteOperationLogs),
    ])
  }

  const readyForDeliveryQuotePivotTable = createPivotTableWatcher(
      api.quote,
      readyForDeliveryQuotesPivotTableKey,
      [
        types.DELIVERIES_READY_FOR_DELIVERY_UPDATE_FILTERS,
        types.DELIVERIES_MARK_AS_DELIVERED_SUCCESS,
        types.DELIVERIES_ADD_DELIVERY_NOTE_SUCCESS,
        types.DELIVERIES_UPDATE_DELIVERY_NOTE_SUCCESS,
      ],
      state => {
        const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
        const filters = getReadyForDelivery(state)
        const {jobStatus} = filters
        const {jobBoard} = filters
        const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

        const params = {}
        const defaultStatuses = [
          QuoteStatus.confirmed,
          QuoteStatus.partDelivered,
          QuoteStatus.invoiced,
          QuoteStatus.paid,
        ]

        const deliveryStatus = [
          DeliveryStatus.none,
          DeliveryStatus.partDelivered,
        ]

        const searchTerm = getSearch(state, readyForDeliveryQuotesPivotTableKey)

        if (getSearchTerm(searchTerm))
          params['searchTerm'] = searchTerm

        params['status'] = isEmpty(jobStatus)
            ? defaultStatuses
            : jobStatus

        params['jobBoardStatus'] = isEmpty(jobBoard) ? [] : jobBoard
        params['deliveryStatus'] = deliveryStatus
        params['requiredStart'] = targetStart
        params['requiredEnd'] = targetEnd

        return params
      },
      {
        default: true,
        company: true,
        contact: true,
        jobCards: true,
        orderitems: {
          quantity: true,
        },
        userfields: true,
        deliveryNotes: {
          default: true,
          deliveryNoteItems: true,
          printOptions: true,
        },
      }
  )

  const deliveryNotesPivotTable = createPivotTableWatcher(
      api.deliveryNote,
      deliveryNotesPivotTableKey,
      [
        types.DELIVERIES_DELIVERY_NOTES_UPDATE_FILTERS,
        types.DELIVERIES_ADD_DELIVERY_NOTE_SUCCESS,
        types.DELIVERIES_UPDATE_DELIVERY_NOTE_SUCCESS,
        types.DELIVERIES_DELETE_DELIVERY_NOTE_SUCCESS,
      ],
      state => getDeliveryNoteParams(state),
      {
        default: true,
        company: true,
        contact: true,
        creator: true,
        modifier: true,
        order: {
          default: true,
          orderItems: {
            id: true,
            quantity: true,
          },
        },
        deliveryNoteItems: {
          id: true,
          quantity: true,
        },
        printOptions: true,
      }
  )

  const deliveriesPivotTable = createPivotTableWatcher(
      api.delivery,
      deliveriesPivotTableKey,
      [
        types.DELIVERIES_DELIVERIES_UPDATE_FILTERS,
      ],
      state => getDeliveryParams(state),
      {
        default: true,
        deliveryResources: true,
        deliveryNote: {
          default: true,
          order: {
            default: true,
            contact: {
              id: true,
              companyId: true,
              forename: true,
              surname: true,
              accountmanager: {
                id: true,
                companyId: true,
                forename: true,
                surname: true,
              },
            },
            salesman: {
              id: true,
              companyId: true,
              forename: true,
              surname: true,
            },
            estimator: {
              id: true,
              companyId: true,
              forename: true,
              surname: true,
            },
          },
        },
        creator: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
        modifier: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
        },
      }
  )

  const deliveryDocumentsPivotTable = createDocumentPivotTableWatcher(
      api.document,
      deliveryDocumentsPivotTableKey,
      [types.DELIVERIES_DOCUMENTS_SET_SELECTED_SUB_FOLDER],
      state => {
        const deliveries = getDeliveries(state)

        const order = isDeliveryDialogDeliveryNew(state)
            ? getDeliveriesQuoteSelectorSelectedOrder(state)
            : get(getDeliveryDialogDeliveryData(state), 'deliveryNote.order')

        const baseFolder = getOrderDocumentsFolderPath(order)
        const subFolder = get(deliveries, 'documentSubFolder') || ''

        return {
          documentProvider: "DEFAULT",
          folderPath: baseFolder + subFolder,
          fromRoot: false,
        }
      }
  )

  function* watcher() {
    yield fork(myWatcher)
    yield fork(readyForDeliveryQuotePivotTable)
    yield fork(deliveryNotesPivotTable)
    yield fork(deliveriesPivotTable)
    yield fork(deliveryDocumentsPivotTable)
  }

  return {
    watcher,
  }
}
